// src/InstaCompetition.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { API } from "./apiBase"; // centralized API resolver

/**
 * InstaCompetition (with ak-status popup + persisted winners + hard-luck overlay)
 * - Loads contacts for date:   GET {API}/search-by-date?date=YYYY-MM-DD
 * - Loads winners for date:    GET {API}/competition-awards?date=YYYY-MM-DD
 * - Select a customer -> wheel appears
 * - Spin; on finish -> shows popup: "ak following", "follow", "None"
 * - Persists winners via        POST {API}/competition-award
 *   payload includes: { name, phone, prize, time, source_date, ak_status, details }
 */
export default function InstaCompetition() {
  const API_BASE = API;

  const [dateStr, setDateStr] = useState(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  });

  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadingWinners, setLoadingWinners] = useState(false);

  const [contacts, setContacts] = useState([]);
  const [filter, setFilter] = useState("");
  const [pickedId, setPickedId] = useState(null);

  const [spinning, setSpinning] = useState(false);
  const [angle, setAngle] = useState(0); // current wheel rotation (deg)
  const [winners, setWinners] = useState([]);

  // Debug (optional)
  const [lastURL, setLastURL] = useState("");
  const [lastStatus, setLastStatus] = useState("");
  const [sampleKeys, setSampleKeys] = useState([]);

  // Hard luck (sad face) overlay
  const [showHardLuck, setShowHardLuck] = useState(false);

  // ak-status choice modal
  const [akModalOpen, setAkModalOpen] = useState(false);
  const [pendingWin, setPendingWin] = useState(null); // { name, phone, prize, time, source_date, details }

  const toastRef = useRef(null);
  const [toast, setToast] = useState("");

  // Prizes (with Hard Luck items)
  const prizes = useMemo(
    () => [
      "Free Main Course (Chicken)",
      "Hard Luck",
      "Free Main Course (Beef)",
      "2 Drinks",
      "Rice",
      "Hard Luck",
      "Noodles",
      "10% Discount",
      "Hard Luck",
      "15% Discount",
      "Free Starter",
      "Free Salad",
      "Hard Luck",
    ],
    []
  );

  // ---------- Helpers ----------
  function clearToastLater() {
    window.clearTimeout(toastRef.current);
    toastRef.current = window.setTimeout(() => setToast(""), 2500);
  }

  function findArrayInObject(obj) {
    if (!obj || typeof obj !== "object") return [];
    if (Array.isArray(obj)) return obj;
    const preferKeys = ["rows", "result", "results", "contacts", "items", "data", "winners"];
    const lowerMap = Object.fromEntries(Object.keys(obj).map((k) => [k.toLowerCase(), k]));
    for (const key of preferKeys) {
      const realKey = lowerMap[key];
      if (realKey && Array.isArray(obj[realKey])) return obj[realKey];
      if (realKey && obj[realKey] && typeof obj[realKey] === "object") {
        const nested = obj[realKey];
        const nestedLower = Object.fromEntries(Object.keys(nested).map((k) => [k.toLowerCase(), k]));
        for (const k2 of preferKeys) {
          const rk2 = nestedLower[k2];
          if (rk2 && Array.isArray(nested[rk2])) return nested[rk2];
        }
      }
    }
    for (const k of Object.keys(obj)) {
      const v = obj[k];
      if (Array.isArray(v)) return v;
      if (v && typeof v === "object") {
        const inner = findArrayInObject(v);
        if (Array.isArray(inner)) return inner;
      }
    }
    return [];
  }

  function normalizeContactRows(rows) {
    return rows.map((r, idx) => {
      const lower = Object.fromEntries(Object.entries(r || {}).map(([k, v]) => [String(k).toLowerCase(), v]));
      const id = r.id ?? lower.id ?? r._id ?? lower._id ?? idx;
      const name = r.name ?? lower.name ?? r.full_name ?? lower.full_name ?? r.customer_name ?? lower.customer_name ?? "";
      const phones = r.phones ?? lower.phones ?? r.phone ?? lower.phone ?? r.mobile ?? lower.mobile ?? "";
      const birthday = r.birthday ?? lower.birthday ?? r.birthdate ?? lower.birthdate ?? r.bday ?? lower.bday ?? null;
      const added_at = r.added_at ?? lower.added_at ?? r.created_at ?? lower.created_at ?? r.inserted_at ?? lower.inserted_at ?? null;
      return { _id: id, name, phones, birthday, added_at, ...r };
    });
  }

  function normalizeWinnerRows(rows) {
    return rows.map((r, idx) => {
      const lower = Object.fromEntries(Object.entries(r || {}).map(([k, v]) => [String(k).toLowerCase(), v]));
      return {
        id: r.id ?? lower.id ?? idx,
        name: r.name ?? lower.name ?? "",
        phone: r.phone ?? lower.phone ?? "",
        prize: r.prize ?? lower.prize ?? "",
        time: r.award_time ?? lower.award_time ?? r.time ?? lower.time ?? "",
        source_date: r.source_date ?? lower.source_date ?? "",
        ak_status: r.ak_status ?? lower.ak_status ?? null,
      };
    });
  }

  // ---------- Loads ----------
  async function loadContactsForDate(d) {
    if (!API_BASE) return;
    setLoadingContacts(true);
    setPickedId(null);
    const url = `${API_BASE}/search-by-date?date=${encodeURIComponent(d)}`;
    setLastURL(url);
    setLastStatus("");

    try {
      const res = await fetch(url, { method: "GET", mode: "cors", credentials: "omit" });
      setLastStatus(`${res.status} ${res.statusText}`);
      if (!res.ok) {
        setContacts([]);
        setSampleKeys([]);
        return;
      }
      const data = await res.json();
      const rows = findArrayInObject(data);
      const normalized = normalizeContactRows(rows);
      setContacts(normalized);
      setSampleKeys(normalized[0] ? Object.keys(normalized[0]) : []);
    } catch (e) {
      setLastStatus(`fetch failed: ${e?.message || e}`);
      setContacts([]);
      setSampleKeys([]);
    } finally {
      setLoadingContacts(false);
    }
  }

  async function loadWinnersForDate(d) {
    if (!API_BASE) return;
    setLoadingWinners(true);
    try {
      const res = await fetch(`${API_BASE}/competition-awards?date=${encodeURIComponent(d)}`, {
        method: "GET",
        mode: "cors",
        credentials: "omit",
      });
      if (!res.ok) {
        setWinners([]);
        return;
      }
      const data = await res.json();
      const rows = findArrayInObject(data);
      setWinners(normalizeWinnerRows(rows));
    } catch {
      setWinners([]);
    } finally {
      setLoadingWinners(false);
    }
  }

  useEffect(() => {
    if (!API_BASE) return;
    loadContactsForDate(dateStr);
    loadWinnersForDate(dateStr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE, dateStr]);

  // ---------- Derived ----------
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter(
      (c) => (c.name ?? "").toLowerCase().includes(q) || (c.phones ?? "").toLowerCase().includes(q)
    );
  }, [contacts, filter]);

  const chosen = useMemo(() => filtered.find((c) => c._id === pickedId) ?? null, [filtered, pickedId]);

  // ---------- Spin (FIXED: pointer alignment + robust result calc) ----------
  // SVG 0° points to the right. Our pointer is at the TOP (12 o'clock) => 270° in SVG coords.
  const POINTER_DEG = 270;

  function spinNow() {
    if (!chosen) {
      setToast("Pick a customer first.");
      clearToastLater();
      return;
    }
    if (spinning) return;

    const n = prizes.length;
    const slice = 360 / n;

    // Random choice BEFORE spinning
    const winnerIndexChosen = Math.floor(Math.random() * n);
    const turns = 5 + Math.floor(Math.random() * 3);

    // Compute the rotation needed so that the chosen slice center lands under our pointer at POINTER_DEG
    const centerDeg = winnerIndexChosen * slice + slice / 2;
    const correction = ((POINTER_DEG - centerDeg) % 360 + 360) % 360; // keep in [0,360)
    const finalDeg = turns * 360 + correction;

    const startAngle = angle; // capture current rotation

    setSpinning(true);
    requestAnimationFrame(() => setAngle((prev) => prev + finalDeg));

    // End of spin after animation time
    window.setTimeout(() => {
      setSpinning(false);

      // Derive the actual index based on the final orientation under the pointer (robust even if state changed)
      const total = (startAngle + finalDeg) % 360;
      const pointerRelative = ((POINTER_DEG - total) % 360 + 360) % 360; // angle of pointer measured on the wheel
      const indexByGeometry = Math.floor(((pointerRelative + slice / 2) % 360) / slice);

      // Safety: if something odd, fall back to the preselected index
      const idx = Number.isFinite(indexByGeometry) ? indexByGeometry : winnerIndexChosen;
      const prize = prizes[idx];

      const win = {
        name: chosen.name || "(no name)",
        phone: chosen.phones || "",
        prize,
        time: new Date().toLocaleString(),
        source_date: dateStr,
        details: chosen,
      };

      // Hard Luck overlay for losses
      if (String(prize).toLowerCase().includes("hard luck")) {
        setShowHardLuck(true);
        setTimeout(() => setShowHardLuck(false), 2200);
      }

      // Save pending result, then open ak-status modal
      setPendingWin(win);
      setAkModalOpen(true);
    }, 3600);
  }

  // ---------- Handle ak-status selection ----------
  async function finalizeWin(akStatus) {
    if (!pendingWin) return;
    const payload = { ...pendingWin, ak_status: akStatus || null };

    // optimistic UI update
    setWinners((prev) => [
      {
        id: `tmp-${Date.now()}`,
        name: payload.name,
        phone: payload.phone,
        prize: payload.prize,
        time: payload.time,
        source_date: payload.source_date,
        ak_status: payload.ak_status,
      },
      ...prev,
    ]);

    // send to backend
    try {
      await fetch(`${API_BASE}/competition-award`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        mode: "cors",
        credentials: "omit",
        body: JSON.stringify(payload),
      });
      // refresh from server (gets real IDs)
      await loadWinnersForDate(dateStr);
    } catch {
      // ignore; optimistic row remains
    }

    setToast(
      payload.ak_status
        ? `Saved (${payload.ak_status}) — ${payload.name} ${
            String(payload.prize).toLowerCase().includes("hard luck") ? "didn't win" : `won: ${payload.prize}`
          }`
        : `${payload.name} ${
            String(payload.prize).toLowerCase().includes("hard luck") ? "didn't win" : `won: ${payload.prize}`
          }`
    );
    setAkModalOpen(false);
    setPendingWin(null);
    clearToastLater();
  }

  if (!API_BASE) {
    return (
      <div className="insta-wrap">
        <div className="toast">API base is not set</div>
      </div>
    );
  }

  return (
    <div className="insta-wrap" style={{ position: "relative" }}>
      {/* Fullscreen Hard Luck overlay */}
      {showHardLuck && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9998,
            background: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#e5e7eb",
            textAlign: "center",
          }}
        >
          <div>
            <div style={{ fontSize: 96, lineHeight: 1 }}>😢</div>
            <div style={{ fontSize: 22, marginTop: 10 }}>Hard luck — better luck next time!</div>
          </div>
        </div>
      )}

      {/* AK status modal */}
      {akModalOpen && pendingWin && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className="card" style={{ width: 360, maxWidth: "90%", padding: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Result Options</div>
            <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 12 }}>
              {pendingWin.name} {pendingWin.phone ? `(${pendingWin.phone})` : ""}
              <br />
              Prize: <b>{pendingWin.prize}</b>
            </div>
            <div style={{ display: "grid", gap: 8 }}>
              <button className="btn" onClick={() => finalizeWin("ak following")}>
                ak following
              </button>
              <button className="btn" onClick={() => finalizeWin("follow")}>
                follow
              </button>
              <button className="btn ghost" onClick={() => finalizeWin(null)}>
                None
              </button>
            </div>
            <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
              <button
                className="btn danger"
                onClick={() => {
                  setAkModalOpen(false);
                  setPendingWin(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Controls / Date */}
      <div className="insta-controls">
        <div className="left" style={{ gap: 8, display: "flex", alignItems: "center", flexWrap: "wrap" }}>
          <button
            className="btn"
            onClick={() => {
              loadContactsForDate(dateStr);
              loadWinnersForDate(dateStr);
            }}
            disabled={loadingContacts || loadingWinners}
          >
            {loadingContacts || loadingWinners ? "⏳ Loading…" : "🔄 Reload"}
          </button>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 13, opacity: 0.9 }}>Date</span>
            <input
              type="date"
              value={dateStr}
              onChange={(e) => setDateStr(e.target.value)}
              style={{
                padding: 6,
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.1)",
                background: "#0f172a",
                color: "#e5e7eb",
              }}
            />
          </label>
          <span className="date-chip">📅 {dateStr}</span>
          <a className="date-chip" href={`${API_BASE}/health`} target="_blank" rel="noreferrer">
            🩺 API
          </a>
        </div>
        <div className="right">
          <input
            className="input"
            placeholder="Search name or phone…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
      </div>

      {/* Debug info (optional) */}
      <div className="card" style={{ padding: 8, marginBottom: 8, fontSize: 12, opacity: 0.9 }}>
        <div>
          <b>URL:</b> {lastURL || "(click Reload to request)"}{" "}
        </div>
        <div>
          <b>Status:</b> {lastStatus || "—"}{" "}
        </div>
        <div>
          <b>Sample contact keys:</b> {sampleKeys.length ? sampleKeys.join(", ") : "—"}
        </div>
      </div>

      <div className="insta-layout">
        {/* Column A: customers */}
        <div className="insta-col card">
          <div className="col-title">Customers ({contacts.length})</div>
          <div className="list-contacts">
            {contacts.length === 0 && <div className="empty">No customers for this date.</div>}
            {contacts.map((c) => (
              <button
                key={c._id}
                className={`row ${pickedId === c._id ? "active" : ""}`}
                onClick={() => setPickedId(c._id)}
                title="Select this customer"
              >
                <div className="row-main">
                  <div className="name">{c.name || "(no name)"}</div>
                  {c.phones && <div className="phone">{c.phones}</div>}
                </div>
                <div className="row-side">Select</div>
              </button>
            ))}
          </div>
        </div>

        {/* Column B: wheel + action (only if customer selected) */}
        <div className="insta-col card">
          <div className="col-title">Prize Wheel</div>

          {!chosen ? (
            <div className="empty" style={{ padding: 16 }}>
              Select a customer on the left to show the wheel.
            </div>
          ) : (
            <>
              <div className="wheel-wrap">
                <div className="pointer">▲</div>
                <svg
                  viewBox="0 0 200 200"
                  className={`wheel ${spinning ? "spinning" : ""}`}
                  style={{ transform: `rotate(${angle}deg)` }}
                >
                  <circle
                    cx="100"
                    cy="100"
                    r="98"
                    fill="rgba(255,255,255,0.06)"
                    stroke="rgba(255,255,255,0.15)"
                    strokeWidth="2"
                  />
                  {prizes.map((label, i) => {
                    const n = prizes.length;
                    const slice = 360 / n;
                    const start = (i * slice) * Math.PI / 180;
                    const end = ((i + 1) * slice) * Math.PI / 180;
                    const x1 = 100 + 98 * Math.cos(start);
                    const y1 = 100 + 98 * Math.sin(start);
                    const x2 = 100 + 98 * Math.cos(end);
                    const y2 = 100 + 98 * Math.sin(end);
                    const largeArc = slice > 180 ? 1 : 0;

                    const mid = (start + end) / 2;
                    const lx = 100 + 60 * Math.cos(mid);
                    const ly = 100 + 60 * Math.sin(mid);

                    return (
                      <g key={i}>
                        <path
                          d={`M100,100 L${x1},${y1} A98,98 0 ${largeArc} 1 ${x2},${y2} Z`}
                          fill="rgba(59,130,246,0.18)"
                          stroke="rgba(59,130,246,0.35)"
                          strokeWidth="1"
                        />
                        <text
                          x={lx}
                          y={ly}
                          fontSize="9"
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fill="#e5e7eb"
                          transform={`rotate(${(i + 0.5) * slice}, ${lx}, ${ly})`}
                        >
                          {label}
                        </text>
                      </g>
                    );
                  })}
                  <circle cx="100" cy="100" r="16" fill="rgba(59,130,246,0.75)" />
                </svg>
              </div>

              <div className="wheel-actions">
                <div className="picked">
                  🎯 <b>{chosen.name}</b> {chosen.phones ? `(${chosen.phones})` : ""}
                </div>
                <button className="btn big" onClick={spinNow} disabled={spinning}>
                  {spinning ? "Spinning…" : "🎡 Spin the Wheel"}
                </button>
              </div>
            </>
          )}

          {/* Winners feed (persisted) */}
          <div className="col-title" style={{ marginTop: 12 }}>
            Winners {loadingWinners ? "⏳" : ""} {winners.length ? `(${winners.length})` : ""}
          </div>
          <div className="winners">
            {winners.length === 0 && <div className="empty">No winners yet.</div>}
            {winners.map((w) => (
              <div key={w.id} className="winner-row">
                <div className="w-prize">🏆 {w.prize}</div>
                <div className="w-name">{w.name}</div>
                {w.phone && <div className="w-phone">{w.phone}</div>}
                <div className="w-time">{w.time}</div>
                {w.ak_status && <div className="w-phone">AK: {w.ak_status}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
